/* NESW tap guard (2026-09-28): a tap while the page or a row is scrolling, gliding or being dragged
   never opens a product. ONE site-wide mechanism for every card: the home rows, the collection grid,
   search, Pair it with, the cart drawer and cart page suggestions, the pack stage, anything linking to
   /products/ or /collections/. Moved out of assets/nesw-collection-row.js, where it guarded only the home
   rows (the collection grid, search and every suggestion row had no guard).
   🔴 It needs to SEE touches. Never add a capture-phase listener on window that stops touch events: the
   retired Klaviyo scroll fix did exactly that and blinded this guard on home and collection pages.
   Buttons inside a card (quick add, swatches, bookmark, sizes) keep their own handlers and are not
   guarded, as before. Loaded on every template from layout/theme.liquid. */
(function () {
  if (window.__neswTapGuard) return;
  window.__neswTapGuard = true;
  var __GUARDED = 'a[href*="/products/"], a[href*="/collections/"]';

  /* V2 — Mobile tap guard (re-enabled, tuned).
     Why this exists: the home rows are `.nesw-row__track` horizontal
     scrollers, and each card is an `<a>`. When the user vertically
     scrolls the page their finger lands inside the carousel, and
     mobile browsers misfire taps on the card because they have to
     pick between three axes (page Y, track X, tap). The collection
     page has no nested scroller so it never has this problem.

     A previous strict guard was too aggressive (blocked legit taps).
     This version uses a single, lenient rule: if the finger moved
     more than ~12px during the touch, or the page/track scrolled at
     all, suppress the next click. Real taps move <5px in practice —
     12px is comfortable headroom and still catches every drag.

     Captures the click in the capture phase so it blocks navigation
     before the `<a>` follows the href. */
  var __touchStartX = null;
  var __touchStartY = null;
  var __touchMaxDist = 0;
  var __touchStartTime = null;
  var __touchStartTrack = null;
  var __touchStartTrackScrollLeft = null;
  var __touchStartScrollTop = 0;
  var __scrollFiredDuringTouch = false;
  /* Tightened 12 -> 8px: home cards were still too easy to trigger while
     scrolling (vs the collection page, which has no nested scroller and
     leans on the browser's native tap/scroll discrimination). Real taps
     move <5px, so 8px is safe headroom but cancels more drift-taps. */
  var __MOVE_THRESHOLD_PX = 8;

  /* 🔴 The page scrolls via <body> on this theme (body.scrollTop is the live
     position; window.pageYOffset / documentElement.scrollTop stay 0). Read all
     three so the guard is correct wherever the scroller actually is. The
     position DELTA (touchstart → click) is the most reliable "did the page
     scroll" signal — immune to scroll-event timing, momentum, and the nested
     horizontal track that makes native tap/scroll discrimination unreliable
     here (the collection page has no nested scroller, which is why it already
     feels right). */
  function __pageScrollTop() {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  var __lastScrollTime = 0;      /* the PAGE's last scroll */
  var __lastRowScrollTime = 0;   /* the last scroll of any other element (a row track) ... */
  var __lastRowScrollEl = null;  /* ... and which element it was */
  var __touchTargetEl = null;
  var __recentScrollAtStart = false;
  var __wasGlidingAtStart = false;
  function __isPage(t) {
    return !t || t === window || t === document || t === document.documentElement || t === document.body;
  }
  /* 🔴 Only a scroll the finger could be part of counts (2026-09-28): the page itself, or the element the
     touch is inside (a row track). An element scrolling ON ITS OWN elsewhere (an auto-advancing gallery,
     a sliding banner, a carousel's autoplay) must never make a still tap look like a scroll. */
  function __rowHoldsTouch(el, touchEl) {
    return !!(el && touchEl && el.contains && el.contains(touchEl));
  }
  function __noteScroll(e) {
    var t = e && e.target;
    if (__isPage(t)) {
      __lastScrollTime = Date.now();
      if (__touchStartTime !== null) __scrollFiredDuringTouch = true;
    } else {
      __lastRowScrollTime = Date.now();
      __lastRowScrollEl = t;
      if (__touchStartTime !== null && __rowHoldsTouch(t, __touchTargetEl)) __scrollFiredDuringTouch = true;
    }
    /* A real scroll (incl. momentum glide, which still fires 'scroll' events
       except right at the moment a finger lands again) is exactly when the
       motion-RAF sampler below needs to be alive. Restarts it if idled out. */
    __startMotionRAF();
  }
  /* capture:true on window hears the page's scroll AND every element's (scroll does not bubble, but the
     capture phase sees it), so rows need no per-element binding. */
  window.addEventListener('scroll', __noteScroll, { capture: true, passive: true });
  /* The nearest ancestor that scrolls sideways (a row track), read at touchstart. */
  function __hScroller(el) {
    for (var n = el; n && n !== document.body; n = n.parentElement) {
      if (n.scrollWidth > n.clientWidth + 2) {
        var ox = getComputedStyle(n).overflowX;
        if (ox === 'auto' || ox === 'scroll') return n;
      }
    }
    return null;
  }

  /* 🔴 rAF motion tracker — the decisive signal for "tap to STOP a momentum
     scroll." iOS throttles/skips 'scroll' events during inertial scrolling, so
     a finger that lands to halt the glide may not see a recent scroll EVENT.
     Polling scrollTop every frame catches the motion regardless: any change =
     the page is (still) moving, so the tap that stops it is treated as a scroll,
     not a navigation. Updates __lastScrollTime (feeds __recentScrollAtStart +
     __scrollTail) and, if a touch is in progress, the during-touch flag. */
  /* 🔴 Gated 2026-09-01 (was an unconditional forever-loop — ~60fps main-thread
     tax on every page load, confirmed via trace). Now only runs while a touch
     or scroll gesture could still be gliding: started by touchstart/scroll,
     and stops rescheduling after 2s with no fresh activity. The next
     touchstart or scroll restarts it; __motionRAFActive guards against
     double-starting two concurrent loops. */
  var __rafPrevTop = __pageScrollTop();
  var __motionRAFActive = false;
  var __motionRAFLastActivity = 0;
  function __motionRAF() {
    var t = __pageScrollTop();
    if (t !== __rafPrevTop) {
      __rafPrevTop = t;
      __lastScrollTime = Date.now();
      /* Detected motion counts as activity: iOS momentum glides can deliver
         scroll EVENTS >450ms apart while the page is still visibly moving —
         without this the sampler idled mid-glide and a later tap read a
         stale __rafPrevTop, swallowing the tap (Fable wave-3 review). */
      __motionRAFLastActivity = __lastScrollTime;
      if (__touchStartTime !== null) __scrollFiredDuringTouch = true;
    }
    /* 🔴 Idle cutoff: was 2000ms (confirmed an unconditional ~60fps tax before
       the 2026-09-01 gating, then a 2s-after-idle tax even gated — measured
       224ms of 500ms main-thread busy time in self-time here alone over a 7s
       scroll session, w3-3-inp.json). Every consumer of the timestamps this
       loop feeds (__recentScrollAtStart, __scrollTail) only ever checks
       freshness within 180-450ms, so nothing downstream can tell the
       difference between stopping at 2000ms and stopping at 450ms — 450 is
       the largest window any consumer reads, kept as the cutoff rather than
       going tighter. */
    if (Date.now() - __motionRAFLastActivity > 450) {
      __motionRAFActive = false;
      return;
    }
    requestAnimationFrame(__motionRAF);
  }
  function __startMotionRAF() {
    __motionRAFLastActivity = Date.now();
    if (__motionRAFActive) return;
    __motionRAFActive = true;
    requestAnimationFrame(__motionRAF);
  }

  document.addEventListener('touchstart', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.touches || !e.touches[0]) return;
    __startMotionRAF();
    __touchStartX = e.touches[0].clientX;
    __touchStartY = e.touches[0].clientY;
    __touchMaxDist = 0;
    __touchStartTime = Date.now();
    __touchTargetEl = e.target;
    __touchStartTrack = __hScroller(e.target);
    __touchStartTrackScrollLeft = __touchStartTrack ? __touchStartTrack.scrollLeft : null;
    __touchStartScrollTop = __pageScrollTop();
    __scrollFiredDuringTouch = false;
    /* Did this tap start while the page was still gliding from a scroll?
       (touching stops iOS momentum, so we won't see a scroll DURING the touch —
       we have to look at how recently one fired.) Widened to 450ms. */
    __recentScrollAtStart = (Date.now() - __lastScrollTime) < 450 ||
      ((Date.now() - __lastRowScrollTime) < 450 && __rowHoldsTouch(__lastRowScrollEl, e.target));
    /* 🔴 THE iOS MOMENTUM FIX: during inertial scrolling iOS THROTTLES both
       scroll events AND requestAnimationFrame, so __lastScrollTime/the rAF
       sampler go stale and the checks above miss a "tap to stop the glide."
       But the page HAS moved since the last rAF frame — so if the live scroll
       position differs from the last sampled position, the page was gliding
       when the finger landed. This catches the stop-tap even when every
       event/rAF was paused. */
    __wasGlidingAtStart = Math.abs(__touchStartScrollTop - __rafPrevTop) > 2;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (__touchStartTime === null || !e.touches || !e.touches[0]) return;
    var dx = e.touches[0].clientX - __touchStartX;
    var dy = e.touches[0].clientY - __touchStartY;
    var d = Math.sqrt(dx * dx + dy * dy);
    if (d > __touchMaxDist) __touchMaxDist = d;
  }, { passive: true });

  /* Capture-phase so we beat the `<a>` navigation. preventDefault on
     a click event on an `<a>` cancels the navigation in every modern
     browser. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var card = e.target.closest(__GUARDED);
    if (!card) {
      __touchStartTime = null;
      __scrollFiredDuringTouch = false;
      __touchMaxDist = 0;
      return;
    }
    /* Allow clicks on interactive descendants (quick-add, swatches,
       bookmark) — those have their own handlers that already call
       stopPropagation/preventDefault as needed. The guard only
       applies to bare navigation on the card itself. */
    if (e.target.closest('.quick-add-btn, .color-swatch, .nesw-bookmark, .related-size-btn, button')) {
      __touchStartTime = null;
      __scrollFiredDuringTouch = false;
      __touchMaxDist = 0;
      return;
    }
    /* Was this click backed by a touch that drifted, or did the page/track
       scroll during the touch? If yes, treat it as an accidental tap during a
       scroll gesture and cancel navigation. Signals (any → cancel):
         • finger drifted past the move threshold
         • a scroll event fired DURING the touch
         • the page actually MOVED (scrollTop delta) — bulletproof, immune to
           event timing/momentum; this is the main fix for slow scrolls
         • the horizontal track actually moved (scrollLeft delta)
         • a scroll fired right before the touch started (tapping mid-glide)
         • a scroll fired right before THIS click (tapping the scroll's tail)
         • a long press that also drifted (a slow drag-to-scroll, not a tap) */
    if (__touchStartTime !== null) {
      var __scrollDelta = Math.abs(__pageScrollTop() - __touchStartScrollTop);
      var __trackDelta = (__touchStartTrack && __touchStartTrackScrollLeft !== null)
        ? Math.abs(__touchStartTrack.scrollLeft - __touchStartTrackScrollLeft) : 0;
      var __scrollTail = (Date.now() - __lastScrollTime) < 180 ||
        ((Date.now() - __lastRowScrollTime) < 180 && __rowHoldsTouch(__lastRowScrollEl, __touchTargetEl));
      var __dur = Date.now() - __touchStartTime;
      /* Also re-check the glide signal at click time: if the live position
         still differs from the last rAF sample, the page is/was moving. */
      var __glideNow = Math.abs(__pageScrollTop() - __rafPrevTop) > 2;
      if (__touchMaxDist > __MOVE_THRESHOLD_PX ||
          __scrollFiredDuringTouch ||
          __scrollDelta > 2 ||
          __trackDelta > 2 ||
          __recentScrollAtStart ||
          __scrollTail ||
          __wasGlidingAtStart ||
          __glideNow ||
          (__dur > 500 && __touchMaxDist > 3)) {
        /* Why the last tap was held back, for debugging on a phone (read window.__neswTapGuardLast). */
        window.__neswTapGuardLast = { at: Date.now(), drift: Math.round(__touchMaxDist), scrollDuringTouch: __scrollFiredDuringTouch, pageMoved: __scrollDelta, rowMoved: __trackDelta, scrollJustBefore: __recentScrollAtStart, scrollTail: __scrollTail, glidingAtStart: __wasGlidingAtStart, glideNow: __glideNow, longDrag: __dur > 500 && __touchMaxDist > 3 };
        e.preventDefault();
        e.stopPropagation();
      }
    }
    __touchStartTime = null;
    __scrollFiredDuringTouch = false;
    __touchMaxDist = 0;
    __recentScrollAtStart = false;
    __wasGlidingAtStart = false;
  }, true);

  document.addEventListener('touchend', function () {
    /* Click fires synchronously after touchend; reset happens in
       the click handler above. */
  }, { passive: true });

})();
